<template>
  <div class="live-claim-feed-dialog">
    <v-form ref="form" v-model="isFormValid">
      <v-row class="live-claim-feed-dialog-rows">
        <v-col cols="12">
          <v-text-field
            v-model="formData.feedTitle"
            label="Feed Title"
            placeholder="Live Claim Feed"
            :rules="[v => !!v || 'Feed title is required']"
            persistent-placeholder />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="6">
          <v-text-field
            v-model.number="formData.turnRate"
            label="Turn Rate (milliseconds)"
            type="number"
            min="1000"
            max="10000"
            :rules="[v => (v >= 1000 && v <= 10000) || 'Turn rate must be between 1000-10000ms']"
            persistent-placeholder />
        </v-col>
        <v-col cols="6">
          <v-text-field
            v-model.number="formData.numberOfLines"
            label="Number of Lines"
            type="number"
            min="1"
            max="20"
            :rules="[v => (v >= 1 && v <= 20) || 'Number of lines must be between 1-20']"
            persistent-placeholder />
        </v-col>
      </v-row>

      <v-divider class="my-4" />

      <h4 class="mb-3">Horizontal Text Labels</h4>
      <v-row>
        <v-col cols="4">
          <v-text-field
            v-model="formData.horizontalTexts.name"
            label="Name Label"
            :rules="[v => !!v || 'Name label is required']"
            persistent-placeholder />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model="formData.horizontalTexts.state"
            label="State Label"
            :rules="[v => !!v || 'State label is required']"
            persistent-placeholder />
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-model="formData.horizontalTexts.benefitAmount"
            label="Benefit Amount Label"
            :rules="[v => !!v || 'Benefit amount label is required']"
            persistent-placeholder />
        </v-col>
      </v-row>

      <v-divider class="my-4" />

      <h4 class="mb-3">Claim Feed Users</h4>
      <div v-for="(user, index) in formData.claimFeedUsers" :key="index" class="user-entry mb-3">
        <v-row>
          <v-col cols="4">
            <v-text-field
              v-model="user.name"
              :label="`User ${index + 1} Name`"
              :rules="[v => !!v || 'Name is required']"
              persistent-placeholder />
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="user.state"
              :label="`User ${index + 1} State`"
              :rules="[v => !!v || 'State is required']"
              persistent-placeholder />
          </v-col>
          <v-col cols="3">
            <v-text-field
              v-model="user.benefitAmount"
              :label="`User ${index + 1} Benefit`"
              :rules="[v => !!v || 'Benefit amount is required']"
              persistent-placeholder />
          </v-col>
          <v-col cols="2">
            <v-btn icon color="error" @click="removeUser(index)" :disabled="formData.claimFeedUsers.length <= 1">
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <v-btn color="primary" outlined @click="addUser" :disabled="formData.claimFeedUsers.length >= 100" class="mb-4">
        <v-icon left>mdi-plus</v-icon>
        Add User
      </v-btn>

      <v-alert v-if="errorMessage" type="error" class="mb-4" dismissible @input="errorMessage = ''">
        {{ errorMessage }}
      </v-alert>
    </v-form>
  </div>
</template>

<script>
  import {
    LIVE_CLAIM_FEED_EVENT_TYPES,
    LIVE_CLAIM_FEED_FIRST_NAMES,
    LIVE_CLAIM_FEED_LAST_INITIALS,
    LIVE_CLAIM_FEED_STATES,
  } from "@/constant";

  export default {
    name: "LiveClaimFeedDialog",
    data() {
      return {
        isFormValid: false,
        formData: {
          feedTitle: "Live Claim Feed",
          turnRate: 3000,
          numberOfLines: 5,
          numberOfSections: 10,
          numberOfUsers: 5,
          horizontalTexts: {
            name: "Name",
            state: "State",
            benefitAmount: "Benefit Amount",
          },
          verticalTexts: {
            name: "Name",
            state: "State",
            benefitAmount: "Benefit Amount",
          },
          claimFeedUsers: [],
        },
        errorMessage: "",
      };
    },
    watch: {
      isFormValid: {
        handler() {
          this.sendError();
        },
      },
      formData: {
        handler() {
          this.validateAndUpdate();
        },
        deep: true,
      },
      "formData.numberOfUsers": {
        handler(newVal) {
          this.adjustUsersList(newVal);
        },
      },
    },
    created() {
      // Generate initial random users
      this.generateInitialUsers();
    },
    beforeMount() {
      this.setupReceiveDataListener();
      this.requestData();
    },
    methods: {
      // Generate initial random users
      generateInitialUsers() {
        const initialCount = 5;
        for (let i = 0; i < initialCount; i++) {
          const randomUser = this.generateRandomUser();
          this.formData.claimFeedUsers.push(randomUser);
        }
      },

      // Generate array of random users
      generateRandomUsersArray(count) {
        const users = [];
        for (let i = 0; i < count; i++) {
          users.push(this.generateRandomUser());
        }

        return users;
      },

      // Amount calculation function
      getAmount() {
        // 7–25 (thousands) → $7,000 … $25,000
        const base = Math.floor(Math.random() * (25 - 7 + 1)) + 7;

        return base * 1000;
      },

      // Random data generation
      generateRandomUser() {
        // Generate random benefit amount using the provided calculation
        const benefitAmount = this.getAmount();

        // Generate unique name
        let name, state, benefit;
        let attempts = 0;
        const maxAttempts = 100;

        do {
          const firstName = LIVE_CLAIM_FEED_FIRST_NAMES[Math.floor(Math.random() * LIVE_CLAIM_FEED_FIRST_NAMES.length)];
          const lastInitial =
            LIVE_CLAIM_FEED_LAST_INITIALS[Math.floor(Math.random() * LIVE_CLAIM_FEED_LAST_INITIALS.length)];
          name = `${firstName} ${lastInitial}.`;
          state = LIVE_CLAIM_FEED_STATES[Math.floor(Math.random() * LIVE_CLAIM_FEED_STATES.length)];
          benefit = `$${benefitAmount.toLocaleString()}`;
          attempts++;
        } while (this.isUserDuplicate(name, state, benefit) && attempts < maxAttempts);

        return { name, state, benefitAmount: benefit };
      },

      isUserDuplicate(name, state, benefitAmount) {
        return this.formData.claimFeedUsers.some(
          user => user.name === name && user.state === state && user.benefitAmount === benefitAmount,
        );
      },

      setupReceiveDataListener() {
        window.onmessage = message => {
          if (message.data.type === LIVE_CLAIM_FEED_EVENT_TYPES.NEED_VALIDATION) {
            this.$refs.form.validate();
          }
          if (message.data.type === LIVE_CLAIM_FEED_EVENT_TYPES.REQUEST_DATA) {
            this.sendData();
          }
          if (message.data.type === LIVE_CLAIM_FEED_EVENT_TYPES.SEND_DATA) {
            const { data } = message.data;
            if (data === "undefined") {
              // Handle case where user exits without saving
              this.updateValue();
            } else {
              this.formData = {
                feedTitle: data.feedTitle || "Live Claim Feed",
                turnRate: data.turnRate || 3000,
                numberOfLines: data.numberOfLines || 5,
                horizontalTexts: data.horizontalTexts || {
                  name: "Name",
                  state: "State",
                  benefitAmount: "Benefit Amount",
                },
                claimFeedUsers: data.claimFeedUsers || this.generateRandomUsersArray(5),
              };
            }
          }
        };
      },
      requestData() {
        window.parent.postMessage(
          {
            mceAction: LIVE_CLAIM_FEED_EVENT_TYPES.REQUEST_DATA,
          },
          "*",
        );
      },
      sendData() {
        window.parent.postMessage(
          {
            mceAction: LIVE_CLAIM_FEED_EVENT_TYPES.SEND_DATA,
            data: this.formData,
          },
          "*",
        );
      },
      async validateAndUpdate() {
        if (this.$refs.form) {
          await this.$refs.form.validate();
          if (this.isFormValid) {
            this.updateValue();
            this.errorMessage = "";
          }
        }
      },
      updateValue() {
        window.parent.postMessage(
          {
            mceAction: LIVE_CLAIM_FEED_EVENT_TYPES.UPDATE_LIVE_CLAIM_FEED_DATA,
            data: this.formData,
          },
          "*",
        );
      },
      sendValidation() {
        window.parent.postMessage(
          {
            mceAction: LIVE_CLAIM_FEED_EVENT_TYPES.VALIDATION,
            data: { isValid: this.isFormValid },
          },
          "*",
        );
      },
      sendError() {
        window.parent.postMessage(
          {
            mceAction: LIVE_CLAIM_FEED_EVENT_TYPES.VALIDATION,
            data: {
              isValid: this.isFormValid,
            },
          },
          "*",
        );
      },
      addUser() {
        if (this.formData.claimFeedUsers.length < 100) {
          const randomUser = this.generateRandomUser();
          this.formData.claimFeedUsers.push(randomUser);
        }
      },
      removeUser(index) {
        if (this.formData.claimFeedUsers.length > 1) {
          this.formData.claimFeedUsers.splice(index, 1);
        }
      },
      adjustUsersList(targetCount) {
        const currentCount = this.formData.claimFeedUsers.length;

        if (targetCount > currentCount) {
          // Add users with random data
          for (let i = currentCount; i < targetCount; i++) {
            const randomUser = this.generateRandomUser();
            this.formData.claimFeedUsers.push(randomUser);
          }
        } else if (targetCount < currentCount) {
          // Remove users
          this.formData.claimFeedUsers = this.formData.claimFeedUsers.slice(0, targetCount);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .live-claim-feed-dialog {
    padding: 20px;

    .user-entry {
      border: 1px solid #e0e0e0;
      border-radius: 4px;
      padding: 16px;
      background-color: #fafafa;
    }
  }
</style>
